<template>
  <div class="video-banner" v-if="items.length && banner.active">
    <div class="video-banner-grid" :style="gridStyle">
      <div class="video-banner-item" v-for="(video, idx) in items" :key="idx">
        <video 
          controls 
          :poster="video.poster"
          class="video-player"
          @error="handleVideoError"
        >
          <source :src="video.src" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoBanner",
  props: {
    banner: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  computed: {
    items() {
      if (!this.banner || !this.banner.active) return [];
      
      const videoFields = [
        this.banner.video01,
        this.banner.video02,
        this.banner.video03,
        this.banner.video04,
        this.banner.video05
      ];
      
      return videoFields
        .filter(videoData => videoData && this.isValidVideoData(videoData))
        .map(videoData => this.parseVideoData(videoData));
    },
    gridStyle() {
      const cols = Math.min(Math.max(this.items.length, 1), 5);
      return { gridTemplateColumns: `repeat(${cols}, 1fr)` };
    }
  },
  methods: {
    isValidVideoData(videoData) {
      if (!videoData) return false;
      
      // Handle direct URL strings
      if (typeof videoData === 'string') {
        // Check if it's a valid URL format
        return videoData.startsWith('http') || videoData.startsWith('/');
      }
      
      // Handle JSON format (backward compatibility)
      if (typeof videoData === 'object') {
        return videoData.download_link || (Array.isArray(videoData) && videoData.length > 0 && videoData[0].download_link);
      }
      
      return false;
    },
    parseVideoData(videoData) {
      // Handle direct URL strings
      if (typeof videoData === 'string') {
        if (videoData.startsWith('http') || videoData.startsWith('/')) {
          return {
            src: videoData,
            poster: null,
            originalName: 'video.mp4'
          };
        }
        
        // Try to parse as JSON (backward compatibility)
        try {
          const parsed = JSON.parse(videoData);
          const video = Array.isArray(parsed) ? parsed[0] : parsed;
          return {
            src: video.download_link,
            poster: video.poster || null,
            originalName: video.original_name || 'video.mp4'
          };
        } catch (e) {
          console.error('Error parsing video data:', e);
          return null;
        }
      }
      
      // Handle object format
      if (typeof videoData === 'object') {
        const video = Array.isArray(videoData) ? videoData[0] : videoData;
        return {
          src: video.download_link,
          poster: video.poster || null,
          originalName: video.original_name || 'video.mp4'
        };
      }
      
      return null;
    },
    handleVideoError(event) {
      console.error('Video loading error:', event.target.src);
    }
  }
};
</script>

<style scoped>
.video-banner {
  margin: auto;
  max-width: 1120px;
  z-index: 100;
}
.video-banner-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  align-items: stretch;
}
.video-banner-item {
  position: relative;
}
.video-player {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  background: #000;
  min-height: 200px;
  object-fit: cover;
}
@media (max-width: 768px) {
  .video-banner-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 480px) {
  .video-banner-grid {
    grid-template-columns: 1fr;
  }
}
</style>
